import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { INVOICE_NAV, resolveInvoiceIntegrationReadiness } from '../../utils/invoiceSchema';
import { iv, ivKpiCardSx, ivSectionTitleSx, ivSurfaceSx } from './invoiceTheme';

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FLOW_STEPS = [
  { n: '1', label: 'Kaynak', hint: 'Eşleş veya sıfırdan' },
  { n: '2', label: 'Taslak', hint: 'Müşteri + kalemler' },
  { n: '3', label: 'Onay', hint: 'Onaya Gönder' },
  { n: '4', label: 'GİB', hint: 'e-Fatura / e-Arşiv' }
];

const KpiCard = ({ title, value, subtitle, accent, onClick }) => (
  <Box sx={ivKpiCardSx(accent)} onClick={onClick}>
    <Typography sx={{ ...ivSectionTitleSx, mb: 0.8 }}>{title}</Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color: iv.text, lineHeight: 1.1 }}>{value}</Typography>
    {subtitle ? (
      <Typography sx={{ mt: 0.6, fontSize: 12.5, color: iv.textMuted, fontWeight: 600 }}>{subtitle}</Typography>
    ) : null}
  </Box>
);

const ActionRow = ({ icon, title, detail, actionLabel, onAction, tone = 'default' }) => {
  const toneColor =
    tone === 'warning' ? iv.warning : tone === 'danger' ? iv.danger : tone === 'success' ? iv.success : iv.primary;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.35,
        borderBottom: `1px solid ${iv.border}`,
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: `${toneColor}14`,
          color: toneColor,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: iv.text }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: iv.textMuted, mt: 0.15 }}>{detail}</Typography>
      </Box>
      {actionLabel ? (
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
          onClick={onAction}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: toneColor,
            border: `1px solid ${iv.border}`,
            bgcolor: '#fff',
            '&:hover': { bgcolor: '#f8fafc', borderColor: iv.borderStrong }
          }}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
};

function InvoiceOverviewPanel({
  stats,
  invoiceSettings,
  matchDataLoading,
  autoMatching,
  onNavigate,
  onRunAutoMatch,
  onRefresh
}) {
  const {
    pendingApproval = 0,
    pendingApprovalAmount = 0,
    missing = 0,
    sendReady = 0,
    error = 0,
    unmatchedMovements = 0,
    toInvoiceTotal = 0
  } = stats || {};

  const readiness = useMemo(
    () => resolveInvoiceIntegrationReadiness(invoiceSettings || {}),
    [invoiceSettings]
  );

  const needsAction = missing + error;
  // Sadece eşleşmemiş banka — onay kuyruğu ayrı kartta
  const faturalandirilacak = unmatchedMovements;

  const actions = [];
  if (pendingApproval > 0) {
    actions.push({
      key: 'approve',
      icon: <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${pendingApproval} fatura onay bekliyor`,
      detail: `Toplam ${formatMoney(pendingApprovalAmount)} ₺ · kontrol edip toplu onaylayabilirsiniz`,
      actionLabel: 'Onaya Git',
      tone: 'success',
      onAction: () => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'pendingApproval' })
    });
  }
  if (unmatchedMovements > 0) {
    actions.push({
      key: 'match',
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${unmatchedMovements} banka hareketi faturalandırılmayı bekliyor`,
      detail: 'Otomatik eşleştirme ile taslak oluşturabilirsiniz',
      actionLabel: autoMatching ? 'Eşleştiriliyor…' : 'Eşleştir',
      tone: 'default',
      onAction: onRunAutoMatch
    });
  }
  if (missing > 0) {
    actions.push({
      key: 'missing',
      icon: <PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${missing} taslakta müşteri bilgisi eksik`,
      detail: 'TCKN/VKN, il veya ilçe tamamlanmalı',
      actionLabel: 'İncele',
      tone: 'warning',
      onAction: () => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'missing' })
    });
  }
  if (error > 0) {
    actions.push({
      key: 'error',
      icon: <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${error} faturada işlem gerekiyor`,
      detail: 'Hatalı kayıtları düzeltin',
      actionLabel: 'İncele',
      tone: 'danger',
      onAction: () => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'error' })
    });
  }
  if (sendReady > 0) {
    actions.push({
      key: 'ready',
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
      title: `${sendReady} fatura onaylandı`,
      detail: readiness.gibReady
        ? 'Onaylandı — GİB’e Gönder ile iletebilirsiniz'
        : 'Onaylandı — GİB gönderimi entegratör kurulunca açılacak',
      actionLabel: 'Listeye Git',
      tone: 'success',
      onAction: () => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'sendReady' })
    });
  }

  const sentCount = Number(stats?.sent || 0);
  if (sentCount > 0) {
    actions.push({
      key: 'sent',
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
      title: `${sentCount} fatura GİB’e gönderildi`,
      detail: 'Giden Belgeler’den durumu takip edin',
      actionLabel: 'Gidenler',
      tone: 'success',
      onAction: () => onNavigate(INVOICE_NAV.OUTBOUND)
    });
  }

  const inboundPending = Number(stats?.inboundPending || 0);
  if (inboundPending > 0) {
    actions.push({
      key: 'inbound',
      icon: <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${inboundPending} gelen fatura yanıt bekliyor`,
      detail: 'Kabul veya red vermeniz gerekiyor',
      actionLabel: 'Gelenler',
      tone: 'warning',
      onAction: () => onNavigate(INVOICE_NAV.INBOUND)
    });
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: iv.text, letterSpacing: '-0.02em' }}>
            Genel Bakış
          </Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
            Eşleş veya sıfırdan → taslak → onay{readiness.gibReady ? ' → GİB' : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            onClick={onRefresh}
            disabled={matchDataLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: iv.border,
              color: iv.text,
              bgcolor: '#fff'
            }}
          >
            {matchDataLoading ? <CircularProgress size={14} /> : 'Yenile'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onRunAutoMatch}
            disabled={autoMatching}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: iv.primary,
              boxShadow: 'none',
              '&:hover': { bgcolor: iv.primaryHover, boxShadow: 'none' }
            }}
          >
            {autoMatching ? 'Eşleştiriliyor…' : 'Yeni Eşleşmeleri Bul'}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          ...ivSurfaceSx,
          px: 1.5,
          py: 1.15,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1
        }}
      >
        {FLOW_STEPS.map((step, idx) => (
          <Box
            key={step.n}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              opacity: idx === 3 && !readiness.gibReady ? 0.55 : 1
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: idx === 3 && !readiness.gibReady ? '#e2e8f0' : iv.primarySoft,
                color: idx === 3 && !readiness.gibReady ? iv.textMuted : iv.primary,
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 800,
                flexShrink: 0
              }}
            >
              {step.n}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: iv.text }}>{step.label}</Typography>
              <Typography sx={{ fontSize: 11, color: iv.textMuted, fontWeight: 600 }}>{step.hint}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.25
        }}
      >
        <KpiCard
          title="Faturalandırılacak"
          value={faturalandirilacak}
          subtitle={toInvoiceTotal > 0 ? `${formatMoney(toInvoiceTotal)} ₺` : 'Bekleyen işlemler'}
          accent={iv.primary}
          onClick={() => onNavigate(INVOICE_NAV.TO_INVOICE)}
        />
        <KpiCard
          title="Onay Bekleyen"
          value={pendingApproval}
          subtitle={pendingApprovalAmount > 0 ? `${formatMoney(pendingApprovalAmount)} ₺` : 'Hazır taslaklar'}
          accent={iv.success}
          onClick={() => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'pendingApproval' })}
        />
        <KpiCard
          title="Onaylandı"
          value={sendReady}
          subtitle={readiness.gibReady ? 'Gönderim için hazır' : 'UKBA onayı tamam'}
          accent={iv.info}
          onClick={() => onNavigate(INVOICE_NAV.APPROVAL, { draftFilter: 'sendReady' })}
        />
        <KpiCard
          title="İşlem Gerekiyor"
          value={needsAction}
          subtitle={needsAction ? 'Eksik bilgi veya hata' : 'Şu an sorun yok'}
          accent={needsAction ? iv.danger : iv.textFaint}
          onClick={() =>
            onNavigate(INVOICE_NAV.APPROVAL, {
              draftFilter: error > 0 ? 'error' : 'missing'
            })
          }
        />
      </Box>

      <Box sx={{ ...ivSurfaceSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            borderBottom: `1px solid ${iv.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: iv.text }}>İşlem Merkezi</Typography>
          <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
            Şimdi ne yapmalısınız?
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {actions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: iv.text }}>
                Şu an müdahale gereken işlem yok
              </Typography>
              <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.6 }}>
                Banka hareketleri geldiğinde veya yeni satış kaydedildiğinde burada görünecek.
              </Typography>
            </Box>
          ) : (
            actions.map((row) => (
              <ActionRow
                key={row.key}
                icon={row.icon}
                title={row.title}
                detail={row.detail}
                actionLabel={row.actionLabel}
                onAction={row.onAction}
                tone={row.tone}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default InvoiceOverviewPanel;
